import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLang, useT, type Lang } from '../../i18n';
import {
  CHINA_GIRLS_WEIGHT_FOR_AGE,
  CHINA_GIRLS_WEIGHT_STANDARD,
  type ChinaGirlsWeightPercentileKey,
} from '../../data/chinaGirlsWeightPercentiles';
import type { MochiWeightPoint } from '../../types/mochi';

type EventCategory = 'social' | 'motor' | 'language' | 'traditional' | 'calendar' | 'diaper';

interface TimelineEvent {
  id: string;
  labelKey: string;
  labelVars?: Record<string, string | number>;
  category: EventCategory;
  startDay: number;
  endDay: number;
  lane: number;
}

interface ReferencePoint {
  day: number;
  p3: number;
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  p97: number;
}

type ReferenceBoundary = 'floor' | ChinaGirlsWeightPercentileKey | 'ceiling';

interface PercentileBand {
  id: string;
  lower: ReferenceBoundary;
  upper: ReferenceBoundary;
  fill: string;
  opacity: number;
}

interface PredictionPoint {
  day: number;
  low: number;
  high: number;
}

interface HoverPosition {
  left: number;
  top: number;
}

interface DiaperSizeGuide {
  size: 'NB' | 'S' | 'M' | 'L';
  minKg: number;
  maxKg: number;
}

const DAYS_TO_TWO = 731;
const WIDTH = 1040;
const HEIGHT = 610;
const LEFT = 64;
const RIGHT = 22;
const TOP = 168;
const BOTTOM = 62;
const PLOT_WIDTH = WIDTH - LEFT - RIGHT;
const PLOT_HEIGHT = HEIGHT - TOP - BOTTOM;
const Y_MIN = 2;
const Y_MAX = 15;

const milestoneEvents: TimelineEvent[] = [
  { id: 'smile', labelKey: 'mochi.events.smile', category: 'social', startDay: 43, endDay: 57, lane: 0 },
  { id: 'laugh', labelKey: 'mochi.events.laugh', category: 'social', startDay: 104, endDay: 195, lane: 1 },
  { id: 'roll', labelKey: 'mochi.events.roll', category: 'motor', startDay: 104, endDay: 223, lane: 2 },
  { id: 'sit', labelKey: 'mochi.events.sit', category: 'motor', startDay: 196, endDay: 284, lane: 0 },
  { id: 'babble', labelKey: 'mochi.events.babble', category: 'language', startDay: 196, endDay: 284, lane: 1 },
  { id: 'mama', labelKey: 'mochi.events.mama', category: 'language', startDay: 285, endDay: 376, lane: 2 },
  { id: 'stand', labelKey: 'mochi.events.stand', category: 'motor', startDay: 285, endDay: 376, lane: 3 },
  { id: 'steps', labelKey: 'mochi.events.steps', category: 'motor', startDay: 316, endDay: 467, lane: 0 },
  { id: 'walk', labelKey: 'mochi.events.walk', category: 'motor', startDay: 365, endDay: 530, lane: 1 },
  { id: 'sentence', labelKey: 'mochi.events.sentence', category: 'language', startDay: 589, endDay: 731, lane: 2 },
];

const categoryOrder: EventCategory[] = ['social', 'motor', 'language', 'traditional', 'calendar', 'diaper'];
const categoryStyles: Record<EventCategory, { fill: string; stroke: string; dot: string }> = {
  social: { fill: 'fill-sky-100/60', stroke: 'stroke-sky-500', dot: 'bg-sky-500' },
  motor: { fill: 'fill-emerald-100/60', stroke: 'stroke-emerald-600', dot: 'bg-emerald-600' },
  language: { fill: 'fill-violet-100/60', stroke: 'stroke-violet-500', dot: 'bg-violet-500' },
  traditional: { fill: 'fill-rose-100/60', stroke: 'stroke-rose-500', dot: 'bg-rose-500' },
  calendar: { fill: 'fill-amber-100/60', stroke: 'stroke-amber-500', dot: 'bg-amber-500' },
  diaper: { fill: 'fill-cyan-100/60', stroke: 'stroke-cyan-600', dot: 'bg-cyan-600' },
};

const diaperSizeGuides: readonly DiaperSizeGuide[] = [
  { size: 'NB', minKg: 0, maxKg: 5 },
  { size: 'S', minKg: 4, maxKg: 8 },
  { size: 'M', minKg: 6, maxKg: 11 },
  { size: 'L', minKg: 9, maxKg: 14 },
];

const percentileBands: readonly PercentileBand[] = [
  { id: 'below-p3', lower: 'floor', upper: 'p3', fill: '#fee2e2', opacity: 0.62 },
  { id: 'p3-p10', lower: 'p3', upper: 'p10', fill: '#fed7aa', opacity: 0.7 },
  { id: 'p10-p25', lower: 'p10', upper: 'p25', fill: '#fef3c7', opacity: 0.82 },
  { id: 'p25-p75', lower: 'p25', upper: 'p75', fill: '#dcfce7', opacity: 0.9 },
  { id: 'p75-p90', lower: 'p75', upper: 'p90', fill: '#fef3c7', opacity: 0.82 },
  { id: 'p90-p97', lower: 'p90', upper: 'p97', fill: '#fed7aa', opacity: 0.7 },
  { id: 'above-p97', lower: 'p97', upper: 'ceiling', fill: '#fee2e2', opacity: 0.62 },
];

const percentileCurves: ReadonlyArray<{
  key: ChinaGirlsWeightPercentileKey;
  color: string;
  width: number;
  dash?: string;
}> = [
  { key: 'p3', color: '#dc2626', width: 1.25, dash: '4 5' },
  { key: 'p10', color: '#ea580c', width: 1, dash: '3 5' },
  { key: 'p25', color: '#ca8a04', width: 1 },
  { key: 'p50', color: '#15803d', width: 2 },
  { key: 'p75', color: '#ca8a04', width: 1 },
  { key: 'p90', color: '#ea580c', width: 1, dash: '3 5' },
  { key: 'p97', color: '#dc2626', width: 1.25, dash: '4 5' },
];

const referencePoints: ReferencePoint[] = CHINA_GIRLS_WEIGHT_FOR_AGE.map((point) => ({
  day: Math.round(point.month * 30.4375),
  p3: point.p3,
  p10: point.p10,
  p25: point.p25,
  p50: point.p50,
  p75: point.p75,
  p90: point.p90,
  p97: point.p97,
}));

export default function MochiGrowthChart({ points }: { points: MochiWeightPoint[] }) {
  const t = useT();
  const { lang } = useLang();
  const svgRef = useRef<SVGSVGElement>(null);
  const chartScrollRef = useRef<HTMLDivElement>(null);
  const sortedPoints = useMemo(
    () => [...points].filter(validPoint).sort((a, b) => Date.parse(a.measured_at) - Date.parse(b.measured_at)),
    [points],
  );
  const birth = startOfDay(new Date(sortedPoints[0].measured_at));
  const end = addDays(birth, DAYS_TO_TWO);
  const rawTodayDay = dayDiff(birth, startOfDay(new Date()));
  const todayDay = clamp(rawTodayDay, 0, DAYS_TO_TWO);
  const actual = sortedPoints.map((point) => ({
    day: clamp(dayDiff(birth, startOfDay(new Date(point.measured_at))), 0, DAYS_TO_TWO),
    value: point.value_kg,
  }));
  const latest = actual[actual.length - 1];
  const [selectedDay, setSelectedDay] = useState(todayDay);
  const [enabled, setEnabled] = useState<Record<EventCategory, boolean>>({
    social: false,
    motor: false,
    language: false,
    traditional: false,
    calendar: false,
    diaper: false,
  });
  const [hoverPosition, setHoverPosition] = useState<HoverPosition | null>(null);

  const calendarEvents = useMemo(() => buildCalendarEvents(birth, end), [birth.getTime(), end.getTime()]);
  const prediction = buildPrediction(latest);
  const diaperEvents = buildDiaperTransitionEvents(prediction);
  const visibleEvents = layoutTimelineEvents(
    [...milestoneEvents, ...calendarEvents, ...diaperEvents].filter((event) => enabled[event.category]),
    t,
  );
  const selectedDate = addDays(birth, selectedDay);
  const selectedAge = formatBabyAge(birth, selectedDate, lang);
  const currentAge = formatBabyAge(birth, startOfDay(new Date()), lang);
  const selectedReference = interpolateReference(selectedDay);
  const selectedPercentiles = percentileCurves.map(({ key, color }) => ({
    key,
    label: key.toUpperCase(),
    color,
    value: selectedReference[key],
  }));
  const selectedPrediction = interpolatePrediction(prediction, selectedDay);
  const selectedDiaperSizes = selectedPrediction ? diaperSizesForRange(selectedPrediction.low, selectedPrediction.high) : [];
  const nearestActual = actual.reduce((nearest, point) => (
    Math.abs(point.day - selectedDay) < Math.abs(nearest.day - selectedDay) ? point : nearest
  ), actual[0]);
  const showNearestActual = Math.abs(nearestActual.day - selectedDay) <= 3;
  const activeEvents = visibleEvents.filter((event) => selectedDay >= event.startDay && selectedDay <= event.endDay);

  const x = (day: number) => LEFT + (day / DAYS_TO_TWO) * PLOT_WIDTH;
  const y = (value: number) => TOP + ((Y_MAX - value) / (Y_MAX - Y_MIN)) * PLOT_HEIGHT;
  const actualPath = linePath(actual.map((point) => ({ x: x(point.day), y: y(point.value) })));
  const percentileBandPaths = percentileBands.map((band) => ({
    ...band,
    path: areaPath(referencePoints.map((point) => ({
      x: x(point.day),
      top: y(referenceBoundaryValue(point, band.upper)),
      bottom: y(referenceBoundaryValue(point, band.lower)),
    }))),
  }));
  const percentileCurvePaths = percentileCurves.map((curve) => ({
    ...curve,
    label: curve.key.toUpperCase(),
    endValue: referencePoints[referencePoints.length - 1][curve.key],
    path: linePath(referencePoints.map((point) => ({ x: x(point.day), y: y(point[curve.key]) }))),
  }));
  function setDayFromPointer(event: ReactPointerEvent<SVGRectElement>, showPanel: boolean) {
    const svg = svgRef.current;
    if (!svg) return;
    const bounds = svg.getBoundingClientRect();
    const viewX = ((event.clientX - bounds.left) / bounds.width) * WIDTH;
    setSelectedDay(Math.round(clamp(((viewX - LEFT) / PLOT_WIDTH) * DAYS_TO_TWO, 0, DAYS_TO_TWO)));
    if (showPanel) {
      const panelWidth = Math.min(288, window.innerWidth - 16);
      const roomOnRight = event.clientX + panelWidth + 18 <= window.innerWidth;
      setHoverPosition({
        left: Math.max(8, roomOnRight ? event.clientX + 14 : event.clientX - panelWidth - 14),
        top: window.innerWidth < 640 ? 8 : clamp(event.clientY - 72, 8, Math.max(8, window.innerHeight - 380)),
      });
    }
  }

  function selectDayFromScrubber(day: number) {
    setSelectedDay(day);
    window.requestAnimationFrame(() => {
      const container = chartScrollRef.current;
      if (!container || container.scrollWidth <= container.clientWidth) return;
      const target = (day / DAYS_TO_TWO) * container.scrollWidth - container.clientWidth / 2;
      container.scrollTo({ left: clamp(target, 0, container.scrollWidth - container.clientWidth), behavior: 'smooth' });
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 border-y border-neutral-200 py-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-wrap gap-x-10 gap-y-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-coffee/70">{t('mochi.selectedDate')}</p>
            <p className="mt-1 text-xl font-semibold text-cocoa">{formatIsoDate(selectedDate)}</p>
            <p className="mt-1 font-mono text-xs text-coffee">{t('mochi.ageAtDate', { age: selectedAge })}</p>
            <p className="mt-1 font-mono text-xs text-coffee">
              {showNearestActual
                ? t('mochi.actualAtDate', { value: nearestActual.value.toFixed(2) })
                : t('mochi.referenceAtDate', { low: selectedReference.p3.toFixed(1), high: selectedReference.p97.toFixed(1) })}
            </p>
            {enabled.diaper && selectedDiaperSizes.length > 0 && (
              <p className="mt-1 font-mono text-xs text-cyan-800">
                {t('mochi.diaperAtDate', { sizes: selectedDiaperSizes.join(' / ') })}
              </p>
            )}
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-coffee/70">{t('mochi.currentAge')}</p>
            <p className="mt-1 text-xl font-semibold text-cocoa">{currentAge}</p>
            <p className="mt-1 font-mono text-xs text-coffee">{t('mochi.asOfToday', { date: formatIsoDate(startOfDay(new Date())) })}</p>
          </div>
        </div>
        <label className="min-w-0 flex-1 lg:max-w-xl">
          <span className="sr-only">{t('mochi.scrubber')}</span>
          <input
            type="range"
            min={0}
            max={DAYS_TO_TWO}
            value={selectedDay}
            onChange={(event) => selectDayFromScrubber(Number(event.target.value))}
            className="w-full accent-orange-500"
          />
        </label>
      </div>

      <fieldset>
        <legend className="font-mono text-xs uppercase tracking-[0.2em] text-coffee/70">{t('mochi.filters')}</legend>
        <div className="mt-3 flex flex-wrap gap-2">
          {categoryOrder.map((category) => (
            <label key={category} className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-neutral-200 bg-white px-3 py-2 font-mono text-xs text-coffee shadow-sm">
              <input
                type="checkbox"
                checked={enabled[category]}
                onChange={() => setEnabled((current) => ({ ...current, [category]: !current[category] }))}
                className="accent-orange-500"
              />
              <span className={`h-2 w-2 rounded-full ${categoryStyles[category].dot}`} />
              {t(`mochi.categories.${category}`)}
            </label>
          ))}
        </div>
        {enabled.diaper && (
          <div className="mt-3 max-w-4xl space-y-1 font-mono text-xs leading-relaxed text-coffee/75">
            <p>{t('mochi.diaperWeightRanges')}</p>
            <p>
              {t('mochi.diaperSizingNote')}{' '}
              <a
                href="https://web.kao.com/tw/merries/guide/select/"
                target="_blank"
                rel="noreferrer"
                className="border-b border-cyan-700 text-cyan-800 hover:border-cyan-950 hover:text-cyan-950"
              >
                {t('mochi.diaperSizingSource')}
              </a>
            </p>
          </div>
        )}
      </fieldset>

      <div ref={chartScrollRef} className="overflow-x-auto border-y border-neutral-200 bg-white/45 py-2">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          role="img"
          aria-label={t('mochi.chartAria')}
          className="h-auto w-full min-w-[1040px] touch-none select-none lg:min-w-0"
        >
          <title>{t('mochi.chartTitle')}</title>
          <desc>{t('mochi.chartAria')}</desc>

          {[2, 4, 6, 8, 10, 12, 14].map((tick) => (
            <g key={tick}>
              <line x1={LEFT} x2={WIDTH - RIGHT} y1={y(tick)} y2={y(tick)} className="stroke-neutral-200" />
              <text x={LEFT - 10} y={y(tick) + 4} textAnchor="end" className="fill-coffee text-[12px] font-mono">{tick}</text>
            </g>
          ))}
          {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((month) => {
            const day = Math.round(month * 30.4375);
            return (
              <g key={month}>
                <line x1={x(day)} x2={x(day)} y1={TOP} y2={HEIGHT - BOTTOM} className="stroke-neutral-200" />
                <text x={x(day)} y={HEIGHT - 32} textAnchor={month === 0 ? 'start' : month === 24 ? 'end' : 'middle'} className="fill-coffee text-[11px] font-mono">
                  {formatIsoDate(addDays(birth, day))}
                </text>
              </g>
            );
          })}

          {percentileBandPaths.map((band) => (
            <path key={band.id} d={band.path} fill={band.fill} fillOpacity={band.opacity} />
          ))}
          {percentileCurvePaths.map((curve) => (
            <g key={curve.key}>
              <path
                d={curve.path}
                fill="none"
                stroke={curve.color}
                strokeWidth={curve.width}
                strokeDasharray={curve.dash}
                strokeOpacity={0.78}
              />
              <text
                x={WIDTH - RIGHT - 4}
                y={y(curve.endValue) + 3}
                textAnchor="end"
                fill={curve.color}
                className="text-[9px] font-mono font-semibold"
              >
                {curve.label}
              </text>
            </g>
          ))}

          {visibleEvents.map((event) => {
            const isPoint = event.startDay === event.endDay;
            const eventX = x(event.startDay);
            const labelX = clamp(eventX + 4, LEFT + 4, WIDTH - RIGHT - Math.max(52, event.displayLabel.length * 12));
            return (
              <g key={event.id}>
                <title>{event.displayLabel}</title>
                {isPoint ? (
                  <line x1={eventX} x2={eventX} y1={TOP} y2={HEIGHT - BOTTOM} strokeDasharray="2 6" className={`${categoryStyles[event.category].stroke} opacity-45`} />
                ) : (
                  <rect
                    x={x(event.startDay)}
                    y={TOP}
                    width={Math.max(2, x(event.endDay) - x(event.startDay))}
                    height={PLOT_HEIGHT}
                    className={`${categoryStyles[event.category].fill}`}
                  />
                )}
                <text x={labelX} y={20 + event.lane * 23} className="fill-cocoa text-[11px] font-mono">
                  {event.displayLabel}
                </text>
                <line x1={eventX} x2={eventX} y1={25 + event.lane * 23} y2={TOP - 4} className={`${categoryStyles[event.category].stroke} opacity-60`} />
              </g>
            );
          })}

          {rawTodayDay >= 0 && rawTodayDay <= DAYS_TO_TWO && (
            <g>
              <line x1={x(todayDay)} x2={x(todayDay)} y1={TOP} y2={HEIGHT - BOTTOM} strokeDasharray="2 6" className="stroke-cocoa stroke-2 opacity-70" />
              <text x={x(todayDay) + 5} y={TOP + 16} className="fill-cocoa text-[11px] font-mono">{t('mochi.today')}</text>
            </g>
          )}

          <path d={actualPath} fill="none" strokeLinecap="round" strokeLinejoin="round" className="stroke-cocoa stroke-[3]" />
          {actual.map((point, index) => (
            <g key={`${point.day}-${point.value}`}>
              <circle cx={x(point.day)} cy={y(point.value)} r="11" className="fill-transparent" />
              <circle cx={x(point.day)} cy={y(point.value)} r="4.5" className="fill-cocoa stroke-white stroke-2" />
              {index === actual.length - 1 && (
                <text x={x(point.day) + 7} y={y(point.value) - 8} className="fill-cocoa text-[12px] font-mono">{point.value.toFixed(2)} kg</text>
              )}
            </g>
          ))}

          <line x1={x(selectedDay)} x2={x(selectedDay)} y1={TOP} y2={HEIGHT - BOTTOM} className="pointer-events-none stroke-orange-500 stroke-2" />
          <circle cx={x(selectedDay)} cy={HEIGHT - BOTTOM} r="6" className="pointer-events-none fill-orange-500 stroke-white stroke-2" />
          <rect
            x={LEFT}
            y={TOP}
            width={PLOT_WIDTH}
            height={PLOT_HEIGHT}
            fill="transparent"
            className="cursor-ew-resize"
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture(event.pointerId);
              setDayFromPointer(event, true);
            }}
            onPointerMove={(event) => {
              if (event.pointerType !== 'touch' || event.currentTarget.hasPointerCapture(event.pointerId)) {
                setDayFromPointer(event, true);
              }
            }}
            onPointerEnter={(event) => setDayFromPointer(event, true)}
            onPointerLeave={(event) => {
              if (!event.currentTarget.hasPointerCapture(event.pointerId)) setHoverPosition(null);
            }}
            onPointerUp={(event) => {
              if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
            }}
            onPointerCancel={() => setHoverPosition(null)}
          />

          <text x={LEFT} y={HEIGHT - 8} className="fill-coffee text-[12px] font-mono">{t('mochi.xAxis')}</text>
          <text x={16} y={TOP - 12} className="fill-coffee text-[12px] font-mono">kg</text>
        </svg>
      </div>

      {hoverPosition &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-50 max-h-[calc(100vh-1rem)] w-[min(18rem,calc(100vw-1rem))] overflow-y-auto rounded-md border border-neutral-700 bg-neutral-900 p-4 text-neutral-100 shadow-float"
            style={{ left: hoverPosition.left, top: hoverPosition.top }}
          >
            <p className="text-base font-semibold">{formatIsoDate(selectedDate)}</p>
            <p className="mt-0.5 font-mono text-xs text-neutral-300">{t('mochi.ageAtDate', { age: selectedAge })}</p>
            <div className="mt-3 space-y-2 border-t border-neutral-700 pt-3 font-mono text-xs">
              {showNearestActual && (
                <TooltipRow dotClass="bg-white" label={t('mochi.legend.actual')} value={`${nearestActual.value.toFixed(2)} kg`} />
              )}
              <TooltipRow dotClass="bg-emerald-400" label={t('mochi.legend.reference')} value={`${selectedReference.p3.toFixed(1)}-${selectedReference.p97.toFixed(1)} kg`} />
              {enabled.diaper && selectedDiaperSizes.length > 0 && (
                <TooltipRow dotClass="bg-cyan-500" label={t('mochi.diaperEstimate')} value={selectedDiaperSizes.join(' / ')} />
              )}
            </div>
            <div className="mt-3 border-t border-neutral-700 pt-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-400">
                {t('mochi.percentilesAtDate')}
              </p>
              <div className="mt-2 grid grid-cols-4 gap-x-3 gap-y-2 font-mono text-[10px]">
                {selectedPercentiles.map((percentile) => (
                  <span key={percentile.key} className="inline-flex items-center gap-1.5">
                    <i className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: percentile.color }} />
                    <span className="text-neutral-400">{percentile.label}</span>
                    <strong className="font-medium text-neutral-100">{percentile.value.toFixed(1)}</strong>
                  </span>
                ))}
              </div>
            </div>
            {activeEvents.length > 0 && (
              <div className="mt-3 space-y-2 border-t border-neutral-700 pt-3">
                {activeEvents.map((event) => (
                  <div key={event.id} className="flex gap-2 text-xs leading-relaxed">
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${categoryStyles[event.category].dot}`} />
                    <span>
                      <strong className="block font-medium">{event.displayLabel}</strong>
                      <span className="font-mono text-neutral-300">{formatEventRange(birth, event)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>,
          document.body,
        )}

      <div className="flex flex-wrap gap-x-5 gap-y-2 font-mono text-xs text-coffee">
        <span className="inline-flex items-center gap-2"><i className="h-0.5 w-5 bg-cocoa" />{t('mochi.legend.actual')}</span>
        <span className="inline-flex items-center gap-2"><i className="h-3 w-5 bg-green-100" />{t('mochi.legend.central')}</span>
        <span className="inline-flex items-center gap-2"><i className="h-3 w-5 bg-amber-100" />{t('mochi.legend.nearTail')}</span>
        <span className="inline-flex items-center gap-2"><i className="h-3 w-5 bg-orange-200" />{t('mochi.legend.outer')}</span>
        <span className="inline-flex items-center gap-2"><i className="h-3 w-5 bg-red-100" />{t('mochi.legend.review')}</span>
      </div>
      <p className="max-w-4xl text-xs leading-relaxed text-coffee/70">
        {t('mochi.referenceSourcePrefix')}{' '}
        <a
          href={CHINA_GIRLS_WEIGHT_STANDARD.sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="border-b border-emerald-700 text-emerald-800 hover:border-emerald-950 hover:text-emerald-950"
        >
          {t('mochi.referenceSourceLink')}
        </a>
      </p>
      <p className="max-w-4xl text-xs leading-relaxed text-coffee/70">{t('mochi.disclaimer')}</p>
    </div>
  );
}

function TooltipRow({ dotClass, label, value }: { dotClass: string; label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="inline-flex min-w-0 items-center gap-2">
        <i className={`h-2 w-2 shrink-0 ${dotClass}`} />
        <span>{label}</span>
      </span>
      <strong className="shrink-0 font-medium">{value}</strong>
    </div>
  );
}

function validPoint(point: MochiWeightPoint): boolean {
  return Number.isFinite(point.value_kg) && point.value_kg > 0 && !Number.isNaN(Date.parse(point.measured_at));
}

function referenceBoundaryValue(point: ReferencePoint, boundary: ReferenceBoundary): number {
  if (boundary === 'floor') return Y_MIN;
  if (boundary === 'ceiling') return Y_MAX;
  return point[boundary];
}

function buildPrediction(latest: { day: number; value: number }): PredictionPoint[] {
  const latestMedian = interpolateReference(latest.day).p50;
  const days = new Set<number>([latest.day, ...referencePoints.map((point) => point.day).filter((day) => day > latest.day), DAYS_TO_TWO]);
  return [...days].sort((a, b) => a - b).map((day) => {
    const ahead = day - latest.day;
    const center = latest.value + interpolateReference(day).p50 - latestMedian;
    const halfRange = 0.18 + (ahead / Math.max(1, DAYS_TO_TWO - latest.day)) * 0.92;
    return { day, low: Math.max(2, center - halfRange), high: Math.min(Y_MAX, center + halfRange) };
  });
}

function interpolateReference(day: number): ReferencePoint {
  return interpolateByDay(referencePoints, day, (left, right, ratio) => ({
    day,
    p3: lerp(left.p3, right.p3, ratio),
    p10: lerp(left.p10, right.p10, ratio),
    p25: lerp(left.p25, right.p25, ratio),
    p50: lerp(left.p50, right.p50, ratio),
    p75: lerp(left.p75, right.p75, ratio),
    p90: lerp(left.p90, right.p90, ratio),
    p97: lerp(left.p97, right.p97, ratio),
  }));
}

function interpolatePrediction(points: PredictionPoint[], day: number): PredictionPoint | null {
  if (day < points[0].day) return null;
  return interpolateByDay(points, day, (left, right, ratio) => ({
    day,
    low: lerp(left.low, right.low, ratio),
    high: lerp(left.high, right.high, ratio),
  }));
}

function interpolateByDay<T extends { day: number }>(points: T[], day: number, mix: (left: T, right: T, ratio: number) => T): T {
  if (day <= points[0].day) return points[0];
  if (day >= points[points.length - 1].day) return points[points.length - 1];
  const rightIndex = points.findIndex((point) => point.day >= day);
  const left = points[rightIndex - 1];
  const right = points[rightIndex];
  return mix(left, right, (day - left.day) / Math.max(1, right.day - left.day));
}

function buildCalendarEvents(birth: Date, end: Date): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  const traditional = new Map([
    ['1-1', 'springFestival'], ['1-15', 'lanternFestival'], ['5-5', 'dragonBoat'],
    ['7-7', 'qixi'], ['8-15', 'midAutumn'], ['9-9', 'doubleNinth'],
  ]);
  const lunarFormatter = new Intl.DateTimeFormat('zh-CN-u-ca-chinese', { month: 'numeric', day: 'numeric' });

  for (let date = birth; date <= end; date = addDays(date, 1)) {
    const day = dayDiff(birth, date);
    const parts = lunarFormatter.formatToParts(date);
    const lunarMonth = parts.find((part) => part.type === 'month')?.value;
    const lunarDay = parts.find((part) => part.type === 'day')?.value;
    const lunarKey = `${lunarMonth}-${lunarDay}`;
    const traditionalKey = traditional.get(lunarKey);
    if (traditionalKey && !String(lunarMonth).includes('bis')) {
      events.push({ id: `${traditionalKey}-${day}`, labelKey: `mochi.events.${traditionalKey}`, category: 'traditional', startDay: day, endDay: day, lane: events.length % 4 });
    }
  }

  for (let year = birth.getFullYear(); year <= end.getFullYear(); year += 1) {
    const fixed: Array<[number, number, string]> = [
      [1, 1, 'newYear'], [3, 8, 'womensDay'], [6, 1, 'childrensDay'], [10, 1, 'nationalDay'], [12, 25, 'christmas'],
    ];
    for (const [month, dayOfMonth, key] of fixed) addCalendarEvent(events, birth, end, new Date(year, month - 1, dayOfMonth), key);
    addCalendarEvent(events, birth, end, nthWeekday(year, 4, 0, 2), 'mothersDay');
    addCalendarEvent(events, birth, end, nthWeekday(year, 5, 0, 3), 'fathersDay');
    addCalendarEvent(events, birth, end, new Date(year, birth.getMonth(), birth.getDate()), 'birthday');
  }
  return events.sort((a, b) => a.startDay - b.startDay).map((event, index) => ({ ...event, lane: index % 4 }));
}

function buildDiaperTransitionEvents(prediction: PredictionPoint[]): TimelineEvent[] {
  return [
    { id: 'diaper-nb-s', from: 'NB', to: 'S', thresholdKg: 5 },
    { id: 'diaper-s-m', from: 'S', to: 'M', thresholdKg: 8 },
    { id: 'diaper-m-l', from: 'M', to: 'L', thresholdKg: 11 },
  ].flatMap(({ id, from, to, thresholdKg }) => {
    const window = findPredictionCrossingWindow(prediction, thresholdKg);
    if (!window) return [];
    return [{
      id,
      labelKey: 'mochi.events.diaperTransition',
      labelVars: { from, to },
      category: 'diaper' as const,
      startDay: window.startDay,
      endDay: window.endDay,
      lane: 0,
    }];
  });
}

function findPredictionCrossingWindow(
  prediction: PredictionPoint[],
  thresholdKg: number,
): { startDay: number; endDay: number } | null {
  let startDay: number | null = null;
  for (let day = Math.ceil(prediction[0].day); day <= DAYS_TO_TWO; day += 1) {
    const point = interpolatePrediction(prediction, day);
    if (!point) continue;
    if (startDay === null && point.high >= thresholdKg) startDay = day;
    if (startDay !== null && point.low >= thresholdKg) return { startDay, endDay: day };
  }
  return startDay === null ? null : { startDay, endDay: DAYS_TO_TWO };
}

function diaperSizesForRange(lowKg: number, highKg: number): string[] {
  return diaperSizeGuides
    .filter((guide) => lowKg <= guide.maxKg && highKg >= guide.minKg)
    .map((guide) => guide.size);
}

function layoutTimelineEvents(
  events: TimelineEvent[],
  translate: (key: string, vars?: Record<string, string | number>) => string,
): Array<TimelineEvent & { displayLabel: string }> {
  const laneEnds = Array.from({ length: 6 }, () => -Infinity);
  return [...events]
    .sort((a, b) => a.startDay - b.startDay || a.endDay - b.endDay)
    .map((event) => {
      const displayLabel = translate(event.labelKey, event.labelVars);
      const labelSpanDays = Math.max(24, (displayLabel.length * 11 / PLOT_WIDTH) * DAYS_TO_TWO);
      let lane = laneEnds.findIndex((endDay) => event.startDay > endDay + 4);
      if (lane < 0) lane = laneEnds.indexOf(Math.min(...laneEnds));
      laneEnds[lane] = event.startDay + labelSpanDays;
      return { ...event, lane, displayLabel };
    });
}

function addCalendarEvent(events: TimelineEvent[], birth: Date, end: Date, date: Date, key: string) {
  if (date < birth || date > end) return;
  const day = dayDiff(birth, date);
  events.push({ id: `${key}-${day}`, labelKey: `mochi.events.${key}`, category: 'calendar', startDay: day, endDay: day, lane: 0 });
}

function nthWeekday(year: number, month: number, weekday: number, occurrence: number): Date {
  const first = new Date(year, month, 1);
  return new Date(year, month, 1 + ((7 + weekday - first.getDay()) % 7) + (occurrence - 1) * 7);
}

function areaPath(points: Array<{ x: number; top: number; bottom: number }>): string {
  if (points.length === 0) return '';
  const top = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.top.toFixed(1)}`).join(' ');
  const bottom = [...points].reverse().map((point) => `L ${point.x.toFixed(1)} ${point.bottom.toFixed(1)}`).join(' ');
  return `${top} ${bottom} Z`;
}

function linePath(points: Array<{ x: number; y: number }>): string {
  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ');
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function dayDiff(start: Date, end: Date): number {
  return Math.round((Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()) - Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())) / 86_400_000);
}

function formatIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatEventRange(birth: Date, event: TimelineEvent): string {
  const start = formatIsoDate(addDays(birth, event.startDay));
  if (event.startDay === event.endDay) return start;
  return `${start} - ${formatIsoDate(addDays(birth, event.endDay))}`;
}

function formatBabyAge(birth: Date, date: Date, lang: Lang): string {
  const end = date < birth ? birth : date;
  let years = end.getFullYear() - birth.getFullYear();
  let yearAnchor = addCalendarYears(birth, years);
  if (yearAnchor > end) {
    years -= 1;
    yearAnchor = addCalendarYears(birth, years);
  }

  let months = (end.getFullYear() - yearAnchor.getFullYear()) * 12 + end.getMonth() - yearAnchor.getMonth();
  let monthAnchor = addCalendarMonths(yearAnchor, months);
  if (monthAnchor > end) {
    months -= 1;
    monthAnchor = addCalendarMonths(yearAnchor, months);
  }
  const days = dayDiff(monthAnchor, end);

  if (lang === 'zh') {
    return years > 0 ? `${years}年${months}个月${days}天` : `${months}个月${days}天`;
  }
  const monthLabel = months === 1 ? 'month' : 'months';
  const dayLabel = days === 1 ? 'day' : 'days';
  if (years === 0) return `${months} ${monthLabel} ${days} ${dayLabel}`;
  return `${years} ${years === 1 ? 'year' : 'years'} ${months} ${monthLabel} ${days} ${dayLabel}`;
}

function addCalendarYears(date: Date, years: number): Date {
  const targetYear = date.getFullYear() + years;
  const lastDay = new Date(targetYear, date.getMonth() + 1, 0).getDate();
  return new Date(targetYear, date.getMonth(), Math.min(date.getDate(), lastDay));
}

function addCalendarMonths(date: Date, months: number): Date {
  const firstOfTarget = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(firstOfTarget.getFullYear(), firstOfTarget.getMonth() + 1, 0).getDate();
  return new Date(firstOfTarget.getFullYear(), firstOfTarget.getMonth(), Math.min(date.getDate(), lastDay));
}

function lerp(start: number, end: number, ratio: number): number {
  return start + (end - start) * ratio;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
